/**
 * Proposta comercial.
 *
 * `calcularTotais` é uma cópia deliberada da função do backend (`models/Proposal.ts`), e um
 * teste guarda as duas contra divergência. A tela precisa recalcular a cada tecla, sem ida ao
 * servidor; o servidor precisa recalcular na hora de gravar, sem confiar no cliente. Uma ida à
 * rede por dígito digitado deixaria o total piscando atrás do que a pessoa vê.
 */

export const SITUACAO_DA_PROPOSTA = {
  RASCUNHO: 'RASCUNHO',
  ENVIADA: 'ENVIADA',
  ACEITA: 'ACEITA',
  RECUSADA: 'RECUSADA',
} as const;

export type SituacaoDaProposta =
  (typeof SITUACAO_DA_PROPOSTA)[keyof typeof SITUACAO_DA_PROPOSTA];

export const ROTULO_DA_SITUACAO: Readonly<Record<SituacaoDaProposta, string>> = {
  RASCUNHO: 'Rascunho',
  ENVIADA: 'Enviada',
  ACEITA: 'Aceita',
  RECUSADA: 'Recusada',
};

export interface TotaisDaProposta {
  vidas: number;
  valorPorVida: number;
  gestao: number;
  clube: number;
  clubeBase: number;
  clubeExcedente: number;
  vidasExcedentes: number;
  total: number;
  totalDoContrato: number;
}

/**
 * Um módulo complementar da proposta — canal de denúncia, IA para plano de ação, o que a
 * negociação pedir. Texto livre, copiado na proposta, e não referência a um catálogo.
 */
export interface ModuloDaProposta {
  name: string;
  description: string | null;
}

/** O que a proposta guarda. Os campos são os mesmos nomes da API. */
export interface DadosDaProposta {
  clientName: string;
  clientCnpj: string | null;
  clientContact: string | null;
  lives: number;
  pricePerLife: number;
  showPricePerLife: boolean;
  includesAso: boolean;
  /** Falso é proposta só do Clube (NR-1), sem a gestão por vida. */
  managementEnabled: boolean;
  clubEnabled: boolean;
  clubBaseFee: number;
  clubBaseLives: number;
  clubExtraPerLife: number;
  contractMonths: number;
  validUntil: string | null;
  readjustmentNote: string | null;
  /**
   * Quem assina. O `consultantId` é só para saber depois quantas propostas cada um mandou — o
   * que sai impresso são as três cópias abaixo, congeladas no dia em que a proposta foi feita.
   */
  consultantId: number | null;
  consultantName: string | null;
  consultantPhone: string | null;
  consultantEmail: string | null;
  status: SituacaoDaProposta;
  notes: string | null;
  modules: readonly ModuloDaProposta[];
}

/** Quem emite. Vem junto na leitura porque é o cabeçalho do documento. */
export interface EmissorDaProposta {
  id: number;
  legalName: string;
  tradeName?: string | null;
  cnpj?: string | null;
}

export interface Proposta extends DadosDaProposta {
  id: number;
  /** Nulo é a própria plataforma: quem vende também faz gestão. */
  providerId: number | null;
  provider?: EmissorDaProposta | null;
  number: string;
  totais: TotaisDaProposta;
  shareToken: string | null;
  sharedAt: string | null;
  viewCount: number;
  firstViewedAt: string | null;
  lastViewedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Uma abertura do link. Sem IP de propósito — ver `models/ProposalView.ts`. */
export interface AberturaDaProposta {
  id: number;
  viewedAt: string;
  userAgent: string | null;
}

/**
 * O que o link público entrega. É um tipo separado do `Proposta` de propósito: o servidor monta
 * esse corpo campo a campo justamente para não mandar o que é interno, e um tipo só faria a tela
 * pública prometer campos que ela nunca recebe.
 */
export interface PropostaPublica
  extends Omit<DadosDaProposta, 'status' | 'notes' | 'consultantId'> {
  number: string;
  provider?: EmissorDaProposta | null;
  totais: TotaisDaProposta;
  createdAt: string;
}

/** DECIMAL volta como string do driver, e string somada concatena em vez de somar. */
function numero(valor: unknown): number {
  const n = Number(valor ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function arredonda(valor: number): number {
  return Math.round(valor * 100) / 100;
}

export function calcularTotais(p: {
  lives: number;
  pricePerLife: number;
  managementEnabled?: boolean;
  clubEnabled: boolean;
  clubBaseFee: number;
  clubBaseLives: number;
  clubExtraPerLife: number;
  contractMonths: number;
}): TotaisDaProposta {
  const vidas = Math.max(0, Math.trunc(numero(p.lives)));
  const valorPorVida = numero(p.pricePerLife);
  // `managementEnabled` ausente e verdadeiro: proposta gravada antes deste campo existir tem
  // gestao, e continua tendo. So quem desligar explicitamente perde a linha.
  const comGestao = p.managementEnabled !== false;
  const gestao = comGestao ? arredonda(vidas * valorPorVida) : 0;

  const vidasBase = Math.max(0, Math.trunc(numero(p.clubBaseLives)));
  const vidasExcedentes = p.clubEnabled ? Math.max(0, vidas - vidasBase) : 0;
  const clubeBase = p.clubEnabled ? numero(p.clubBaseFee) : 0;
  const clubeExcedente = arredonda(vidasExcedentes * numero(p.clubExtraPerLife));
  const clube = arredonda(clubeBase + clubeExcedente);

  const total = arredonda(gestao + clube);
  const meses = Math.max(1, Math.trunc(numero(p.contractMonths)));

  return {
    vidas,
    valorPorVida,
    gestao,
    clube,
    clubeBase,
    clubeExcedente,
    vidasExcedentes,
    total,
    totalDoContrato: arredonda(total * meses),
  };
}

/** Reais, com os centavos. Proposta sem centavo parece estimativa. */
export function emReais(valor: number): string {
  return numero(valor).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  });
}
